import {
  actionsKeys,
  formatAmount,
  permissionActions,
} from "@/lib/utils";
import {
  setCurrentFinanceYear,
  setInvoiceDueDate,
} from "@/redux/slices";
import { RootState } from "@/redux/store";
import { routes } from "@/routes/utils";
import {
  useGetBillingStat,
  useGetPositionApprovalLimits,
} from "@/services";
import { FileInput, FileLock2, FileSearch, FileX2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "@/components/Toast";

const useBilling = () => {
  const navigate = useNavigate();
  const [openTabs, setOpenTabs] = useState<string[]>(["billing"]);
  const dispatch = useDispatch();
  const accessPermission = useSelector(
    (state: RootState) => state.currentData.accessPermission
  );

  const communityData = useSelector(
    (state: RootState) => state.currentData.communityData
  );

  const currentFinanceYear = useSelector(
    (state: RootState) => state.currentData.currentFinanceYear
  );

  const [billingCards, setBillingCards] = useState([
    {
      id: "summary",
      data: [
        {
          label: "#Monthly HOA Fees",
          value: 0,
          apiKey: "monthly",
        },
        {
          label: "#Yearly HOA Fees",
          value: 0,
          apiKey: "yearly",
        },
        {
          label: "#Adhoc HOA Fees",
          value: 0,
          apiKey: "adhoc",
        },
      ],
      baseApiKey: "billingConfigurations",
      dataHeader: ["Transaction Type", "Amt."],
      title: "Billing Configuration",
      link: `${routes.billingConfig}`,
      module: actionsKeys?.BILLING_CONFIG?.toLowerCase(),
      accessKeys: permissionActions.READ,
    },
    {
      id: "valueObject",
      data: [
        {
          label: "Monthly HOA Fees",
          value: ["0", "0"],
          apiKey: ["monthly", "monthlyAmount"],
          baseApiKey: ["billing", "amount"],
        },
        {
          label: "Yearly HOA Fees",
          value: ["0", "0"],
          apiKey: ["yearly", "yearlyAmount"],
          baseApiKey: ["billing", "amount"],
        },
        {
          label: "Adhoc HOA Fees",
          value: ["0", "0"],
          apiKey: ["adhoc", "adhocAmount"],
          baseApiKey: ["billing", "amount"],
        },
      ],
      baseApiKey: "pendingPosting",
      dataHeader: ["Transaction Type", "Billing", "Amt."],
      title: "Pending For Posting",
      link: `${routes.pendingPosting}`,
      module: actionsKeys?.BILLING_DOCUMENT?.toLowerCase(),
      accessKeys: permissionActions.READ,
    },
    {
      id: "valueObject",
      data: [
        {
          label: "Monthly HOA Fees",
          value: ["0"],
          apiKey: ["monthlyAmount"],
        },
        {
          label: "Yearly HOA Fees",
          value: ["0"],
          apiKey: ["yearlyAmount"],
        },
        {
          label: "Adhoc HOA Fees",
          value: ["0"],
          apiKey: ["adhocAmount"],
        },
      ],
      baseApiKey: "postedBillAmounts",
      dataHeader: ["Transaction Type", "Amt."],
      title: "Billed For the Period",
      link: `${routes.billedForPeriod}`,
      module: actionsKeys?.BILLING_DOCUMENT?.toLowerCase(),
      accessKeys: permissionActions.READ,
    },
    {
      id: "summary",
      data: [
        {
          label: "Last Monthly Run",
          value: "",
          apiKey: "monthly",
        },
        {
          label: "Last Yearly Run",
          value: "",
          apiKey: "yearly",
        },
        {
          label: "Last Adhoc Run",
          value: "",
          apiKey: "adhoc",
        },
      ],
      baseApiKey: "lastBillingLogs",
      title: "Billing Log",
      link: `${routes.billingLog}`,
      module: actionsKeys?.BILLING_LOGS?.toLowerCase(),
      accessKeys: permissionActions.READ,
    },
  ]);

  const [userCardList, setUserCardList] = useState<any>([
    {
      title: communityData?.bomPosition?.positionName,
      subTitle: "Position",
      icon: FileSearch,
    },
    {
      title: "",
      subTitle: "Approved Upto",
      icon: FileInput,
      apiKey: "approvalLimit",
    },
    {
      title: "",
      subTitle: "Yearly Limit",
      icon: FileInput,
      apiKey: "totalLimit",
    },
    {
      title: "",
      subTitle: "Approved Amt.",
      icon: FileX2,
      apiKey: "approvedToDate",
    },
    {
      title: "",
      subTitle: "Remaining Amt.",
      icon: FileLock2,
      apiKey: "remainingLimit",
    },
  ]);

  const breadcrumbData = useMemo(
    () => [
      {
        link: `/${routes.bom}/${routes.finance}`,
        name: `Activity Dashboard`,
      },
      {
        link: "",
        name: <span className="text-primary">{"Billing"}</span>,
      },
    ],
    []
  );

  const { data: userData, isLoading: isUserDataLoading } =
    useGetPositionApprovalLimits({
      positionId: communityData?.bomPosition?.bomPositionId,
      year: currentFinanceYear,
      queryParams: {
        enabled: !!(
          communityData?.bomPosition?.bomPositionId && currentFinanceYear
        ),
      },
    });

  useEffect(() => {
    if (userData) {
      const updatedList = userCardList.map((card: any) => ({
        ...card,
        ...(card?.apiKey
          ? {
              title:
                card?.apiKey != "positionName"
                  ? formatAmount(userData?.[card.apiKey])
                  : userData?.[card.apiKey] || "",
            }
          : {}),
      }));

      setUserCardList(updatedList);
    }
  }, [userData]);

  // useEffect(() => {
  //   dispatch(setCurrentFinanceYear(moment().format("YYYY")));
  // }, []);

  const onYearChange = (e: any) => {
    dispatch(setCurrentFinanceYear(e));
  };

  const onAccordionChange = (e: any) => {
    setOpenTabs(e);
  };

  const onExpandCollapse = () => {
    if (openTabs?.length) {
      setOpenTabs([]);
    } else {
      setOpenTabs(["payment", "invoice"]);
    }
  };

  const onCreateCardClick = (link: string) => () => {
    if (communityData) {
      navigate(link);
    } else {
      toast.error("Please select community");
    }
  };

  const onCardClick = (link: string) => () => {
    dispatch(setInvoiceDueDate(null));
    navigate(link);
  };

  const onBackClick = () => {
    navigate(`/${routes.bom}/${routes.finance}`);
  };

  const { data: billingStat, isLoading: isBillingStatLoading } =
    useGetBillingStat({
      communityId: communityData?.communityId,
      billingYear: currentFinanceYear,
      queryParams: {
        enabled: !!communityData?.communityId,
      },
    });

  useEffect(() => {
    if (!billingStat) return;

    const updatedCards = billingCards.map((card: any) => {
      const { id, data: cardData, baseApiKey, apiKey } = card;
      const statData = (billingStat as any)?.data;

      if (id === "summary") {
        const updatedData = cardData.map((item: any) => ({
          ...item,
          value: statData?.[baseApiKey]?.[item.apiKey] ?? 0,
        }));

        return {
          ...card,
          data: updatedData,
          access: accessPermission?.[card.module]?.[card.accessKeys],
        };
      }

      if (id === "valueObject") {
        const updatedData = cardData.map((item: any) => {
          const { apiKey: keys, baseApiKey: baseKeys } = item;

          const values = keys.map((key: string, index: number) => {
            const baseKey = baseKeys?.[index];

            const rawValue = baseKey
              ? statData?.[baseApiKey]?.[baseKey]?.[key]
              : statData?.[baseApiKey]?.[key];

            return baseKey?.toLowerCase()?.includes("amount") ||
              key?.toLowerCase()?.includes("amount")
              ? formatAmount(rawValue ?? 0)
              : rawValue ?? 0;
          });

          return {
            ...item,
            value: values,
            access: !!accessPermission?.[card.module]?.[card.accessKeys],
          };
        });

        return {
          ...card,
          data: updatedData,
          access: !!accessPermission?.[card.module]?.[card.accessKeys],
        };
      }

      return {
        ...card,
        value: statData?.[apiKey] ?? 0,
        access: !!accessPermission?.[card.module]?.[card.accessKeys],
      };
    });

    setBillingCards(updatedCards);
  }, [billingStat, accessPermission]);

  return {
    communityData,
    currentFinanceYear,
    onYearChange,
    billingCards,
    onAccordionChange,
    openTabs,
    onExpandCollapse,
    onCreateCardClick,
    onCardClick,
    breadcrumbData,
    onBackClick,
    userCardList,
    isBillingStatLoading,
    isUserDataLoading,
  };
};

export default useBilling;
